import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SwitchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LanguageLabel = styled.span<{ $active: boolean }>`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.$active ? '#667eea' : '#2c3e50'};
  transition: color 0.3s ease;
  cursor: pointer;
  
  &:hover {
    color: #667eea;
  }
  
  /* Style pour le menu mobile */
  @media (max-width: 768px) {
    color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.7)'};
    font-weight: 600;
    
    &:hover {
      color: white;
    }
  }
`;

const SwitchTrack = styled.div<{ $isEnglish: boolean }>`
  width: 60px;
  height: 30px;
  background: ${props => props.$isEnglish ? '#667eea' : '#e0e0e0'};
  border-radius: 15px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: ${props => props.$isEnglish ? '#5a6fd8' : '#d0d0d0'};
  }
  
  /* Style pour le menu mobile */
  @media (max-width: 768px) {
    background: ${props => props.$isEnglish ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1), 0 4px 15px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(10px);
    
    &:hover {
      background: ${props => props.$isEnglish ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.2)'};
      border-color: rgba(255, 255, 255, 0.3);
    }
  }
`;

const SwitchThumb = styled.div<{ $isEnglish: boolean }>`
  width: 26px;
  height: 26px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: ${props => props.$isEnglish ? '32px' : '2px'};
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
`;

const LanguageSwitcher: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const isEnglish = language === 'en';

  const toggleLanguage = () => {
    setLanguage(isEnglish ? 'fr' : 'en');
  };

  return (
    <SwitchContainer>
      <SwitchWrapper>
        <LanguageLabel 
          $active={!isEnglish}
          onClick={() => setLanguage('fr')}
        >
          FR
        </LanguageLabel>
        
        <SwitchTrack 
          $isEnglish={isEnglish} 
          onClick={toggleLanguage}
          role="switch"
          aria-checked={isEnglish}
          aria-label={`Switch to ${isEnglish ? 'French' : 'English'}`}
        >
          <SwitchThumb $isEnglish={isEnglish} />
        </SwitchTrack>
        
        <LanguageLabel 
          $active={isEnglish}
          onClick={() => setLanguage('en')}
        >
          EN
        </LanguageLabel>
      </SwitchWrapper>
    </SwitchContainer>
  );
};

export default LanguageSwitcher;